import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Downshift from 'downshift';
import PickupDestination from './PickupDestination.validations';
import * as locationActions from '../../redux/actions/location.actions';
import * as requestsActions from '../../redux/actions/requests.actions';
import * as formFlowActions from '../../redux/actions/formFlow.actions';
import * as infoboxActions from '../../redux/actions/infobox.actions';
import * as mapActions from '../../redux/actions/map.actions';
import * as componentActions from './redux/actions/component.actions';
import { handleSuggestionChange } from '../common/SuggestionsInput/SuggestionsInput';
import { pickupDestinationCollapse, pickupDestinationExpanded } from '../../flags/collapse.flag';
import { islockedByBillerVerified, islockedByPcr } from '../../flags/disabled.flag';
import { showHospitalToHospitalWarning } from '../../flags/service.flag';
import { reasonRequiredForFlaggingLocation, unsetLocationFlag } from '../../messages';
import { SCHEDULE_STATE } from '../../enums';
import { sortMultiETA } from '../../helpers/sortHelper';

const billerVerifiedMsg = 'You must unset Prebiller Verified to change location.';
const zollMsg = 'Trip was exported to Zoll. Location can only change to a matching facility.';
const hospitalToHospitalWarnings = [
  '<p id="hospitalToHospitalWarning">Acute Hospital to Hospital transfers require additional reasoning in the section below.</p>',
];
const warningCount = state => (showHospitalToHospitalWarning(state) ? hospitalToHospitalWarnings.length : 0);

const sortEta = state => [...state.eta.offeredEtas].sort(sortMultiETA);

class PickupDestinationContainer extends React.Component {
  state = {
    subLabel: '',
    flagReasonDialog: {
      open: false,
      locationType: '',
    },
    flagUnsetDialog: {
      open: false,
      locationType: '',
    },
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.lockedByBillerVerified !== nextProps.lockedByBillerVerified && !nextProps.lockedByBillerVerified) {
      this.setState({
        subLabel: '',
      });
    }
  }

  handlePickupLocationChange = (query, downshiftState) => {
    const {
      changeRequiresNewETA,
      request,
      keyDownETAField,
      lockedByPcr,
      queryPickupFacilitiesOnly,
      selectPickupLocation,
      clearNothing,
      lockedByBillerVerified,
      queryPickupLocations,
      clearPickupLocation,
    } = this.props;

    if (
      changeRequiresNewETA &&
      request.needsAssignment &&
      downshiftState.type !== Downshift.stateChangeTypes.mouseUp &&
      downshiftState.type !== Downshift.stateChangeTypes.blurInput
    ) {
      keyDownETAField();
      return;
    }

    if (lockedByPcr) {
      this.setState({
        subLabel: zollMsg,
      });

      handleSuggestionChange(queryPickupFacilitiesOnly, selectPickupLocation, clearNothing)(query, downshiftState);
      return;
    }

    if (lockedByBillerVerified) {
      this.setState({
        subLabel: billerVerifiedMsg,
      });

      return;
    }

    handleSuggestionChange(queryPickupLocations, selectPickupLocation, clearPickupLocation)(query, downshiftState);
  };

  handlePickupLocationClick = () => {
    const { lockedByPcr, queryPickupFacilitiesOnly, lockedByBillerVerified } = this.props;

    if (lockedByPcr) {
      queryPickupFacilitiesOnly();
      this.setState({
        subLabel: zollMsg,
      });

      return;
    }

    if (lockedByBillerVerified) {
      this.setState({
        subLabel: billerVerifiedMsg,
      });
    }
  };

  handleDestinationChange = (query, downshiftState) => {
    const {
      changeRequiresNewETA,
      request,
      queryDestinationFacilitiesOnly,
      selectDestinationLocation,
      clearNothing,
      queryDestinations,
      clearDestinationLocation,
      lockedByPcr,
      lockedByBillerVerified,
    } = this.props;

    if (
      changeRequiresNewETA &&
      request.needsAssignment &&
      downshiftState.type !== Downshift.stateChangeTypes.mouseUp &&
      downshiftState.type !== Downshift.stateChangeTypes.blurInput
    ) {
      this.props.keyDownETAField();
      return;
    }

    if (lockedByPcr) {
      this.setState({
        subLabel: zollMsg,
      });

      handleSuggestionChange(
        queryDestinationFacilitiesOnly,
        selectDestinationLocation,
        clearNothing,
      )(query, downshiftState);

      return;
    }

    if (lockedByBillerVerified) {
      this.setState({
        subLabel: billerVerifiedMsg,
      });

      return;
    }

    handleSuggestionChange(
      queryDestinations,
      selectDestinationLocation,
      clearDestinationLocation,
    )(query, downshiftState);
  };

  handleDestinationClick = () => {
    if (this.props.lockedByPcr) {
      this.props.queryDestinationFacilitiesOnly();
      this.setState({
        subLabel: zollMsg,
      });

      return;
    }

    if (this.props.lockedByBillerVerified) {
      this.setState({
        subLabel: billerVerifiedMsg,
      });
    }
  };

  handleToggleERBound = event => {
    if (this.props.changeRequiresNewETA) {
      this.props.keyDownETAField();
      return;
    }

    this.props.toggleERBound(event.target.checked);
  };

  handleLocationFlagSet = locationType => {
    this.setState({
      flagReasonDialog: {
        open: true,
        locationType,
      },
    });
  };

  handleLocationFlagUnset = locationType => {
    this.setState({
      flagUnsetDialog: {
        open: true,
        locationType,
      },
    });
  };

  handleFlagReasonDialogClose = (ok, value) => {
    if (ok) {
      if (value['pickup']) {
        this.props.setPickupDangerousFlag(value['pickup']);
      } else if (value['destination']) {
        this.props.setDestinationDangerousFlag(value['destination']);
      }
    }
    this.setState({
      flagReasonDialog: {
        open: false,
        locationType: '',
      },
    });
  };

  handleFlagUnsetDialogClose = (ok, locationType) => {
    if (ok) {
      if (locationType === 'pickup') {
        this.props.unsetPickupDangerousFlag();
      } else if (locationType === 'destination') {
        this.props.unsetDestinationDangerousFlag();
      }
    }
    this.setState({
      flagUnsetDialog: {
        open: false,
        locationType: '',
      },
    });
  };

  render() {
    return (
      <PickupDestination
        handlePickupLocationChange={this.handlePickupLocationChange}
        handlePickupLocationClick={this.handlePickupLocationClick}
        handleDestinationChange={this.handleDestinationChange}
        handleDestinationClick={this.handleDestinationClick}
        handleLocationFlagSet={this.handleLocationFlagSet}
        handleLocationFlagUnset={this.handleLocationFlagUnset}
        handleFlagReasonDialogClose={this.handleFlagReasonDialogClose}
        handleFlagUnsetDialogClose={this.handleFlagUnsetDialogClose}
        setDestinationConfirmation={this.props.setDestinationConfirmation}
        handleToggleERBound={this.handleToggleERBound}
        defaultPickupSec={window.ambulnzConfig.constants.REQUEST_DEFAULT_AT_PICKUP_SECS}
        defaultDropoffSec={window.ambulnzConfig.constants.REQUEST_DEFAULT_AT_DROPOFF_SECS}
        showPickup={true}
        showDestination={!this.props.isNonTransport}
        {...this.props}
        {...this.state}
      />
    );
  }
}

PickupDestinationContainer.propTypes = {
  queryPickupLocations: PropTypes.func.isRequired,
  queryDestinations: PropTypes.func.isRequired,
  selectPickupLocation: PropTypes.func.isRequired,
  selectDestinationLocation: PropTypes.func.isRequired,
  clearPickupLocation: PropTypes.func.isRequired,
  clearDestinationLocation: PropTypes.func.isRequired,
  toggleERBound: PropTypes.func.isRequired,
  keyDownETAField: PropTypes.func.isRequired,
  changeRequiresNewETA: PropTypes.bool.isRequired,
  lockedByBillerVerified: PropTypes.bool.isRequired,
  lockedByPcr: PropTypes.bool.isRequired,
  queryPickupFacilitiesOnly: PropTypes.func.isRequired,
  queryDestinationFacilitiesOnly: PropTypes.func.isRequired,
  clearNothing: PropTypes.func.isRequired,
  request: PropTypes.shape().isRequired,
};

function mapStateToProps(state) {
  const sortedEtas = sortEta(state);
  const atPickupOfferedSegment = sortedEtas[0]?.segments?.find(s => s.state === SCHEDULE_STATE.AT_PICK_UP);
  const atDropoffOfferedSegment = sortedEtas[0]?.segments?.find(s => s.state === SCHEDULE_STATE.AT_DROP_OFF);
  return {
    location: state.location,
    pickupLocation: state.location.from,
    selectedPickupLocation: state.location.fromSelected,
    pickupLocations: state.location.fromItems,
    destination: state.location.to,
    selectedDestinationLocation: state.location.toSelected,
    destinations: state.location.toItems,
    shouldCollapse: pickupDestinationCollapse(state),
    isERBound: state.location.erBound,
    changeRequiresNewETA: state.formFlow.changeRequiresNewETA,
    manualPickupSecs: state.location.manualPickupSecs || atPickupOfferedSegment?.seconds,
    manualDropOffSecs: state.location.manualDropOffSecs || atDropoffOfferedSegment?.seconds,
    searchPickupLocationInProgress: state.location.searchPickupLocationInProgress,
    searchDestinationInProgress: state.location.searchDestinationInProgress,
    hideEstTime: window.ambulnzConfig.hideEstTime,
    user: state.user.model,
    loadedWillCall: state.requests.loadedWillCall,
    triggerValidation: state.formFlow.triggerValidation,
    request: state.requests.request,
    isNonTransport: state.requests.isNonTransport,
    destinationAutoFocus: state.location.destinationAutoFocus,
    isPickupResidence: state.map.isPickupResidence,
    isDestinationResidence: state.map.isDestinationResidence,
    pickupResidenceDisabled: state.map.pickupResidenceDisabled,
    destinationResidenceDisabled: state.map.destinationResidenceDisabled,
    expanded: pickupDestinationExpanded(state),
    lockedByBillerVerified: islockedByBillerVerified(state),
    lockedByPcr: islockedByPcr(state),
    pickupDepartments: state.location.pickupDepartments,
    destinationDepartments: state.location.destinationDepartments,
    pickupDepartmentId: state.requests.pickupDepartmentId,
    destinationDepartmentId: state.requests.destinationDepartmentId,
    warnings: hospitalToHospitalWarnings,
    showWarning: showHospitalToHospitalWarning(state),
    warningCount: warningCount(state),
    isDestinationConfirmed: state.location.isDestinationConfirmed,
    isDispatcher: state.user.model.isDispatcher,
    destinationConfirmationRequired:
      state.user.model.isRequester && state.agency.requesterAgencyGroup?.requireDestinationConfirm,
    reasonRequiredForFlaggingLocationTitle: reasonRequiredForFlaggingLocation.title,
    reasonRequiredForFlaggingLocationBody: reasonRequiredForFlaggingLocation.body,
    unsetLocationFlagTitle: unsetLocationFlag.title,
    unsetLocationFlagBody: unsetLocationFlag.body,
    pickupDangerFlag: state.location.pickupDangerFlag,
    pickupDangerFlagReason: state.location.pickupDangerFlagReason,
    destinationDangerFlag: state.location.destinationDangerFlag,
    destinationDangerFlagReason: state.location.destinationDangerFlagReason,
  };
}

export default connect(mapStateToProps, {
  ...locationActions,
  ...requestsActions,
  ...formFlowActions,
  ...infoboxActions,
  ...mapActions,
  ...componentActions,
})(PickupDestinationContainer);
